import type { ExamSessionFragment } from "./generated";
import { api } from "./generated";

/**
 * AIDEV-NOTE: Zrcadlo practiceQuizAttemptApi — mutace vracejí celou novou session a odpověď
 * se zapisuje rovnou do cache getExamSession místo invalidace tagů (druhý request po
 * každém kroku zkoušky). Hooky featury se importují odsud, ne z api/generated, aby
 * enhancement vždy proběhl před prvním použitím. [TD-304]
 */
// AIDEV-NOTE: Patch musí trefit TU položku cache, ze které komponenty čtou. Při běžné
// zkoušce je to getExamSession({ bookId }), při návratu z historie
// getExamSessionById({ sessionId }) — proto je argument sessionId mutace zároveň
// směrovkou. Bez toho by se sebehodnocení po kliknutí nepřekreslilo. [TD-359]
const upsertSession = (args: { bookId: string; sessionId?: string | null }, session: ExamSessionFragment) => {
  const sessionId = args.sessionId ?? "";

  if (sessionId.length > 0)
    return examSessionApi.util.updateQueryData("getExamSessionById", { sessionId }, draft => {
      draft.examSessionById = session;
    });

  return examSessionApi.util.updateQueryData("getExamSession", { bookId: args.bookId }, draft => {
    draft.examSession = session;
  });
};

/** Tvar, který `examPositionSet` posílá zpět, a zároveň jediná část session, kterou mění. */
interface IExamPosition {
  id: string;
  commissionBlock?: number | null;
  commissionQuestion?: number | null;
}

// AIDEV-NOTE: Pozice se patchuje po polích, ne přes `upsertSession` — odpověď mutace veze
// tři pole místo celé session (schválně, jde ~25× za běh), takže by zápisem celku smazala
// z cache snapshot bloků i výsledek. Kontrola `id` chrání proti patchi do běhu, který
// mezitím v cache vystřídal jiný. [TD-361]
const patchPosition = (session: IExamPosition | null | undefined, position: IExamPosition) => {
  if (session?.id !== position.id) return;

  session.commissionBlock = position.commissionBlock;
  session.commissionQuestion = position.commissionQuestion;
};

export const examSessionApi = api.enhanceEndpoints({
  endpoints: {
    examAttemptStart: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertSession(args, data.examAttemptStart));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    examAttemptDiscard: {
      // `getState` se schválně nedestrukturuje — `unbound-method` je tu error a metoda
      // odtržená od svého objektu by přišla o `this`.
      async onQueryStarted(args, lifecycle) {
        const { dispatch, queryFulfilled } = lifecycle;

        try {
          await queryFulfilled;

          // AIDEV-NOTE: Jediná mutace zkoušení, která `upsertSession` použít NEMŮŽE —
          // vrací `Boolean`, ne session, protože běh po ní neexistuje. Bez tohohle patche
          // by `BookExam` po „Začít znovu" četl z cache smazaný běh a rozhodl se podle
          // něj. Nuluje se obě položky: běžná i adresná, ať zbytek featury nezáleží na
          // tom, kterou zrovna kdo odebírá. Patch do neexistující položky je no-op. [TD-361]
          dispatch(
            examSessionApi.util.updateQueryData("getExamSession", { bookId: args.bookId }, draft => {
              if (draft.examSession?.id === args.sessionId) draft.examSession = null;
            }),
          );
          dispatch(
            examSessionApi.util.updateQueryData("getExamSessionById", { sessionId: args.sessionId }, draft => {
              draft.examSessionById = null;
            }),
          );

          // AIDEV-NOTE: Historie se patchuje taky, i když ji zahození nespustilo — běh, co
          // došel ke komisi, v ní řádek MÁ a spec §1 F říká, že po „Začít znovu" zmizí.
          // `useExamHistory` čte `currentData`, které je po znovuotevření modálu hned
          // k dispozici ze staré cache, takže bez tohohle by smazaný běh probliknul, než
          // doběhne refetch. Argumenty nejsou známé dopředu (limit/offset/bookId), proto
          // se berou z cache — api nemá tagTypes, invalidace tu neexistuje. [TD-361]
          const historyArgs = examSessionApi.util.selectCachedArgsForQuery(lifecycle.getState(), "getExamHistory");

          for (const cached of historyArgs)
            dispatch(
              examSessionApi.util.updateQueryData("getExamHistory", cached, draft => {
                draft.examHistory = draft.examHistory.filter(row => row.id !== args.sessionId);
              }),
            );
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    examStepSet: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertSession(args, data.examStepSet));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    examPositionSet: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const position = data.examPositionSet;

          // Směrovkou je stejně jako v `upsertSession` argument `sessionId` mutace —
          // patch musí trefit tu položku cache, ze které komponenty čtou. [TD-359]
          const sessionId = args.sessionId ?? "";
          dispatch(
            sessionId.length > 0
              ? examSessionApi.util.updateQueryData("getExamSessionById", { sessionId }, draft => {
                  patchPosition(draft.examSessionById, position);
                })
              : examSessionApi.util.updateQueryData("getExamSession", { bookId: args.bookId }, draft => {
                  patchPosition(draft.examSession, position);
                }),
          );
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    examQuestionRate: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertSession(args, data.examQuestionRate));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    examRateSpeech: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertSession(args, data.examRateSpeech));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    examSpeechClipsSet: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertSession(args, data.examSpeechClipsSet));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    examFinish: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertSession(args, data.examFinish));

          // AIDEV-NOTE: has_tested zapisuje server v examFinish; tady se jen propíše do
          // cache, ať se tečka nad tabem rozsvítí bez refetche. Prázdný patch znamená,
          // že progress v cache není (nebo je null) — pak se stáhne celý. [TD-304]
          const patch = dispatch(
            examSessionApi.util.updateQueryData("getUserBookProgress", { bookId: args.bookId }, draft => {
              if (draft.userBookProgress !== null && draft.userBookProgress !== undefined)
                draft.userBookProgress.hasTested = true;
            }),
          );
          if (patch.patches.length === 0) {
            void dispatch(
              examSessionApi.endpoints.getUserBookProgress.initiate(
                { bookId: args.bookId },
                { subscribe: false, forceRefetch: true },
              ),
            );
          }
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
  },
});

export const {
  useGetExamSessionQuery,
  useGetExamSessionByIdQuery,
  useGetExamHistoryQuery,
  useGetExamCategoriesQuery,
  useExamAttemptStartMutation,
  useExamAttemptDiscardMutation,
  useExamStepSetMutation,
  useExamPositionSetMutation,
  useExamQuestionRateMutation,
  useExamRateSpeechMutation,
  useExamSpeechClipsSetMutation,
  useExamFinishMutation,
} = examSessionApi;
